import { ConfirmModal, MoreButton } from '@/components';
import { ProjectResourceGuard } from '@/features/authentication/_components/ProjectResourceGuard';
import { useProject } from '@/features/project/_hooks/project';
import { useDeleteFundBusiness } from '@/features/project/projects/detail/fund-bussiness/_hooks/fund-business';
import { useId } from '@/hooks';
import { useMessage } from '@/providers';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useProjectDetailDrawerState } from '../../_components/ProjectDrawerStateProvider';

type Props = {
  fundBusinessId: string;
};

export const FundBusinessTableActions: FC<Props> = ({ fundBusinessId }) => {
  const { t } = useTranslation();
  const { openMessage } = useMessage();
  const { setDrawerState } = useProjectDetailDrawerState();
  const { id: projectId } = useId();
  const project = useProject(projectId);
  const { mutateAsync: deleteFundBusiness } = useDeleteFundBusiness(projectId);

  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);

  const closeDeleteModal = () => {
    setIsOpenDeleteModal(false);
  };

  const handleDeleteBusinessTable = async (fundBusinessId: string) => {
    try {
      await deleteFundBusiness(fundBusinessId);
      await Promise.all([
        openMessage('success', t('message-delete-completed')),
        (async () => {
          closeDeleteModal();
        })(),
      ]);
    } catch {
      await openMessage('error', t('message-delete-failed'));
    }
  };

  return (
    <ProjectResourceGuard onlyEdit>
      {project.data && !project.data.isLocked && (
        <>
          <MoreButton
            items={[
              { key: 'edit', label: t('edit') },
              { key: 'delete', label: t('delete'), danger: true },
            ]}
            onClick={({ key }) => {
              switch (key) {
                case 'edit':
                  setDrawerState({
                    open: true,
                    isEditing: true,
                    type: 'fund-business',
                    fundBusinessId,
                  });
                  break;
                case 'delete':
                  setIsOpenDeleteModal(true);
                  break;
              }
            }}
          />
          <ConfirmModal
            open={isOpenDeleteModal}
            onCancel={closeDeleteModal}
            title={t('delete-confirmation')}
            disclaimerText={t('delete-confirmation-disclaimer')}
            onOk={async () => {
              await handleDeleteBusinessTable(fundBusinessId);
            }}
          />
        </>
      )}
    </ProjectResourceGuard>
  );
};
